/**
 * Navigation shell — the side switch (Code / Occult), the side's menu of worlds
 * and tools (sidebar), the active tool's header, search (tools, pages and the
 * content behind the tools), and the small-screen menu drawer.
 *
 * Everything here is derived from registry.js; main.js owns switching.
 */

import { ROOMS, toolById, groupById, roomById, groupsInRoom, toolsInGroup } from './registry.js';
import { escapeHtml } from './utils/html.js';
import { safeReadJSON } from './utils/storage.js';
import { toolNoteHtml } from './utils/tool-note.js';
import { toolTrailHtml } from './utils/tool-head.js';
import { searchTools, searchPages, searchContent, contentTarget, contentMeta } from './utils/search.js';
import { initTermCards } from './utils/term-cards.js';

const DRAWER_QUERY = '(max-width: 1099px)';

let handlers = {
  onSelectTool: () => {},
  onSelectSide: () => {},
  onSelectPage: () => {},
  onOpenTool: () => {},
};

let appEl;
let sidebarEl;
let backdropEl;
let toggleEl;
let drawerReturnFocus = null;

// ─── Sidebar menu ────────────────────────────────────────────────────────────
// One menu per side; CSS shows the one matching .app[data-side]. Each world is
// a heading that opens to its tools (2026-10-23, once Code passed 45 tools):
// the active tool's world is open, and a world opened by hand stays open,
// remembered on the device, until it is closed.

const OPEN_WORLDS_KEY = 'unveil:open-worlds';

/** Worlds opened by hand. */
let openWorlds = new Set();
/** The active tool's world, and whether it has been closed by hand since that tool opened. */
let activeGroup = null;
let activeClosed = false;

const isOpen = (groupId) => (groupId === activeGroup ? !activeClosed : openWorlds.has(groupId));

function applyOpenWorlds() {
  sidebarEl.querySelectorAll('.index__group').forEach((section) => {
    const open = isOpen(section.dataset.group);
    section.classList.toggle('index__group--current', section.dataset.group === activeGroup);
    section.querySelector('.index__toggle').setAttribute('aria-expanded', String(open));
    section.querySelector('.index__list').hidden = !open;
  });
}

function toggleWorld(groupId) {
  const open = !isOpen(groupId);
  if (groupId === activeGroup) activeClosed = !open;
  else if (open) openWorlds.add(groupId);
  if (!open) openWorlds.delete(groupId);
  try {
    localStorage.setItem(OPEN_WORLDS_KEY, JSON.stringify([...openWorlds]));
  } catch {
    /* ignored — the worlds stay open for this visit */
  }
  applyOpenWorlds();
}

function renderSidebar() {
  const saved = safeReadJSON(OPEN_WORLDS_KEY, []);
  openWorlds = new Set(Array.isArray(saved) ? saved.filter((id) => groupById[id]) : []);

  sidebarEl.innerHTML = ROOMS.map(
    (room) => `
    <nav class="index" data-side="${room.id}" aria-label="${room.label} tools">
      ${groupsInRoom(room.id)
        .map((group) => {
          const tools = toolsInGroup(group.id);
          return `
        <section class="index__group" data-group="${group.id}">
          <h2 class="index__group-title">
            <button type="button" class="index__toggle" id="index-group-${group.id}" aria-expanded="false" aria-controls="index-list-${group.id}">
              <svg class="index__chevron" viewBox="0 0 12 12" aria-hidden="true"><path d="M4.5 2.5 8 6l-3.5 3.5" /></svg>
              <span class="index__group-label">${group.label}</span>
              <span class="index__count">${tools.length}<span class="visually-hidden"> tools</span></span>
            </button>
          </h2>
          <ul class="index__list" id="index-list-${group.id}" aria-labelledby="index-group-${group.id}" hidden>
            ${tools.map((t) => `<li><a class="index__link" href="/${t.id}" data-tool="${t.id}">${t.label}</a></li>`).join('')}
          </ul>
        </section>`;
        })
        .join('')}
    </nav>`,
  ).join('');
  applyOpenWorlds();

  sidebarEl.addEventListener('click', (e) => {
    const toggle = e.target.closest('.index__toggle');
    if (toggle) {
      toggleWorld(toggle.closest('.index__group').dataset.group);
      return;
    }
    const link = e.target.closest('.index__link');
    if (!link) return;
    e.preventDefault();
    closeDrawer({ restoreFocus: false });
    handlers.onSelectTool(link.dataset.tool);
  });
}

// ─── Side switch ─────────────────────────────────────────────────────────────

/** Reflect the current side in the switch (null on the portal). */
export function setActiveSide(side) {
  document.querySelectorAll('[data-side-link]').forEach((link) => {
    if (link.dataset.sideLink === side) link.setAttribute('aria-current', 'true');
    else link.removeAttribute('aria-current');
  });
}

function setupSideSwitch() {
  document.querySelectorAll('[data-side-link]').forEach((link) =>
    link.addEventListener('click', (e) => {
      e.preventDefault();
      if (link.getAttribute('aria-current') === 'true') return;
      closeDrawer({ restoreFocus: false });
      handlers.onSelectSide(link.dataset.sideLink);
    }),
  );
}

/** Mark the active tool in the index, open its world, and keep it in view. */
export function setActiveTool(toolId) {
  const group = toolById[toolId]?.group ?? null;
  if (group !== activeGroup) {
    activeGroup = group;
    activeClosed = false;
  }
  applyOpenWorlds();
  sidebarEl.querySelectorAll('.index__link').forEach((link) => {
    if (link.dataset.tool === toolId) {
      link.setAttribute('aria-current', 'page');
      const box = sidebarEl.getBoundingClientRect();
      const r = link.getBoundingClientRect();
      if (r.top < box.top + 48 || r.bottom > box.bottom - 24) {
        link.scrollIntoView({ block: 'center' });
      }
    } else {
      link.removeAttribute('aria-current');
    }
  });
}

// ─── Tool header ─────────────────────────────────────────────────────────────

export function renderToolHead(toolId) {
  const head = document.getElementById('tool-head');
  const tool = toolById[toolId];
  if (!head || !tool) return;
  const group = groupById[tool.group];
  const room = roomById[group.room];

  head.dataset.tool = toolId;
  head.innerHTML = `
    ${toolTrailHtml(room, group)}
    <div class="tool-head__row">
      <div class="tool-head__titleline">
        <h1 class="tool-head__title">${tool.label}</h1>
        <button type="button" class="info-btn tool-head__info" aria-expanded="false" aria-controls="tool-head-note" aria-label="About ${tool.label}" title="About this tool" hidden>i</button>
      </div>
      <button type="button" class="tool-head__share" data-action="share" title="Copy a link to this tool with its current input">
        <svg class="icon" viewBox="0 0 20 20" aria-hidden="true"><path d="M8.5 11.5a3 3 0 0 0 4.2 0l2.6-2.6a3 3 0 0 0-4.2-4.2l-.9.9M11.5 8.5a3 3 0 0 0-4.2 0l-2.6 2.6a3 3 0 0 0 4.2 4.2l.9-.9" /></svg>
        <span>Copy link</span>
      </button>
    </div>
    <div class="tool-head__note" id="tool-head-note" hidden></div>`;

  // The note on the tool (data/tool-notes.js) and its glossary terms load separately, so the app
  // shell stays small; the note is in the page at once, open or not, so a search engine reads it
  Promise.all([import('./data/tool-notes.js'), import('./utils/glossary.js'), import('./data/tool-about.js')]).then(([{ TOOL_NOTES }, { termsOfTool }, { TOOL_ABOUT }]) => {
    const note = TOOL_NOTES[toolId];
    if (!note || head.dataset.tool !== toolId) return;
    head.querySelector('.tool-head__note').innerHTML = toolNoteHtml(note, tool, termsOfTool(toolId), TOOL_ABOUT[toolId]);
    head.querySelector('.tool-head__info').hidden = false;
  });
}

// ─── Drawer (small screens) ──────────────────────────────────────────────────

function isDrawerMode() {
  return matchMedia(DRAWER_QUERY).matches;
}

// Only what is shown: the other side's menu and closed worlds have no boxes
function focusables() {
  return [...sidebarEl.querySelectorAll('a[href], button:not([disabled])')].filter((el) => el.getClientRects().length);
}

function trapFocus(e) {
  if (e.key === 'Escape') {
    e.preventDefault();
    closeDrawer();
    return;
  }
  if (e.key !== 'Tab') return;
  const items = focusables();
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

function openDrawer() {
  if (!isDrawerMode()) return;
  drawerReturnFocus = document.activeElement;
  appEl.classList.add('app--drawer-open');
  backdropEl.hidden = false;
  toggleEl.setAttribute('aria-expanded', 'true');
  sidebarEl.setAttribute('role', 'dialog');
  sidebarEl.setAttribute('aria-modal', 'true');
  sidebarEl.addEventListener('keydown', trapFocus);
  const current = sidebarEl.querySelector('.index__link[aria-current="page"]') || focusables()[0];
  current?.focus();
}

export function closeDrawer({ restoreFocus = true } = {}) {
  if (!appEl?.classList.contains('app--drawer-open')) return;
  appEl.classList.remove('app--drawer-open');
  backdropEl.hidden = true;
  toggleEl.setAttribute('aria-expanded', 'false');
  sidebarEl.removeAttribute('role');
  sidebarEl.removeAttribute('aria-modal');
  sidebarEl.removeEventListener('keydown', trapFocus);
  if (restoreFocus) drawerReturnFocus?.focus();
  drawerReturnFocus = null;
}

function setupDrawer() {
  toggleEl.addEventListener('click', () => {
    if (appEl.classList.contains('app--drawer-open')) closeDrawer();
    else openDrawer();
  });
  backdropEl.addEventListener('click', () => closeDrawer());
  // Leaving drawer mode (e.g. rotating a tablet) must not strand an open modal state
  matchMedia(DRAWER_QUERY).addEventListener('change', () => {
    if (!isDrawerMode()) closeDrawer({ restoreFocus: false });
  });
}

// ─── Search ──────────────────────────────────────────────────────────────────
// Tools, then pages, then what lies behind the tools (utils/search.js); the
// content index loads the first time the field is used.

let contentIndex = [];
let contentLoad = null;

function loadContentIndex(onLoaded) {
  contentLoad ??= import('./data/search-index.js')
    .then((m) => {
      contentIndex = m.CONTENT_INDEX;
      onLoaded();
    })
    .catch(() => {
      contentLoad = null;
    });
}

function matchesFor(query) {
  const content = searchContent(contentIndex, query, 6);
  return [
    ...searchTools(query, content.length ? 6 : 8).map((t) => ({
      label: t.label,
      meta: `${roomById[groupById[t.group].room].label} / ${groupById[t.group].label}`,
      open: () => handlers.onSelectTool(t.id),
    })),
    ...searchPages(query).map((p) => ({ label: p.label, meta: 'Page', open: () => handlers.onSelectPage(p.id, '') })),
    ...content.map((entry) => {
      const target = contentTarget(entry);
      return {
        label: entry[2],
        meta: contentMeta(entry),
        content: true,
        open: () => (target.page ? handlers.onSelectPage(target.page, target.hash) : handlers.onOpenTool(target.tool, target.state)),
      };
    }),
  ];
}

function setupSearch() {
  const input = document.getElementById('tool-search');
  const results = document.getElementById('tool-search-results');
  if (!input || !results) return;
  let matches = [];
  let activeIdx = -1;

  function setActive(idx) {
    activeIdx = idx;
    results.querySelectorAll('.search__result').forEach((el, i) => el.classList.toggle('is-active', i === idx));
    const active = results.querySelector('.search__result.is-active');
    if (active) input.setAttribute('aria-activedescendant', active.id);
    else input.removeAttribute('aria-activedescendant');
  }

  function close() {
    results.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    setActive(-1);
  }

  function render() {
    if (!input.value.trim()) {
      matches = [];
      close();
      return;
    }
    matches = matchesFor(input.value);
    const firstContent = matches.findIndex((m) => m.content);
    results.innerHTML = matches.length
      ? matches
          .map(
            (m, i) =>
              `${i === firstContent ? '<p class="search__group" role="presentation">Inside the tools</p>' : ''}<button type="button" class="search__result" id="search-result-${i}" role="option" data-index="${i}" tabindex="-1">
              <span class="search__result-label">${escapeHtml(m.label)}</span>
              <span class="search__result-meta">${escapeHtml(m.meta)}</span>
            </button>`,
          )
          .join('')
      : '<p class="search__empty">Nothing matches. Try a cipher, a term or a name, like “moon”, “orb” or “Vehuiah”.</p>';
    results.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    setActive(matches.length ? 0 : -1);
  }

  function commit(match) {
    input.value = '';
    close();
    input.blur();
    closeDrawer({ restoreFocus: false });
    match.open();
  }

  input.addEventListener('input', () => {
    loadContentIndex(() => {
      if (document.activeElement === input) render();
    });
    render();
  });
  input.addEventListener('focus', () => {
    loadContentIndex(() => {
      if (document.activeElement === input && input.value.trim()) render();
    });
    if (input.value.trim()) render();
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      input.value = '';
      close();
      input.blur();
    } else if (e.key === 'ArrowDown' && matches.length) {
      e.preventDefault();
      setActive((activeIdx + 1) % matches.length);
    } else if (e.key === 'ArrowUp' && matches.length) {
      e.preventDefault();
      setActive((activeIdx - 1 + matches.length) % matches.length);
    } else if (e.key === 'Enter' && matches.length) {
      e.preventDefault();
      commit(matches[Math.max(activeIdx, 0)]);
    }
  });

  // mousedown keeps focus on the input so the click lands before blur closes the list
  results.addEventListener('mousedown', (e) => e.preventDefault());
  results.addEventListener('click', (e) => {
    const btn = e.target.closest('.search__result');
    if (btn) commit(matches[Number(btn.dataset.index)]);
  });
  input.addEventListener('blur', close);

  // "/" anywhere outside a text field focuses search
  document.addEventListener('keydown', (e) => {
    if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
    const el = document.activeElement;
    if (el && (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.isContentEditable)) return;
    e.preventDefault();
    input.focus();
    input.select();
  });
}

// ─── Init ────────────────────────────────────────────────────────────────────

export function initNavigation(opts) {
  handlers = { ...handlers, ...opts };
  appEl = document.querySelector('.app');
  sidebarEl = document.getElementById('sidebar');
  backdropEl = document.getElementById('sidebar-backdrop');
  toggleEl = document.getElementById('index-toggle');

  renderSidebar();
  setupSideSwitch();
  setupDrawer();
  setupSearch();
  initTermCards();

  // The ⓘ beside a tool's title opens its note
  document.getElementById('tool-head')?.addEventListener('click', (e) => {
    const btn = e.target.closest('.tool-head__info');
    if (!btn) return;
    const note = document.getElementById('tool-head-note');
    note.hidden = !note.hidden;
    btn.setAttribute('aria-expanded', String(!note.hidden));
  });
}
